import React from 'react'
import {
  Card,
  CardContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
} from '@mui/material'
import Grid from '@mui/material/Grid'
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material'
import { NSIPSPrescription, NSIPSDrug } from '@/features/internal/types/nsips'
import { NSIPS_LABELS } from '@/constants/nsips'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { DrugSearchField } from '@/components/molecules/DrugSearchField'

interface PrescriptionSectionProps {
  prescription: NSIPSPrescription
  rpIndex: number
  errors: Record<string, string>
  canDelete: boolean
  onUsageChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onDaysChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onDrugFieldChange: (
    drugIndex: number,
    field: keyof NSIPSDrug
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void
  onDrugSelect: (drugIndex: number) => (drugName: string) => void
  onAddDrug: () => void
  onRemoveDrug: (drugIndex: number) => void
  onRemovePrescription: () => void
}

export const PrescriptionSection: React.FC<PrescriptionSectionProps> =
  React.memo(
    ({
      prescription,
      rpIndex,
      errors,
      canDelete,
      onUsageChange,
      onDaysChange,
      onDrugFieldChange,
      onDrugSelect,
      onAddDrug,
      onRemoveDrug,
      onRemovePrescription,
    }) => {
      return (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="subtitle1">RP {rpIndex + 1}</Typography>
              {canDelete && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={onRemovePrescription}
                  sx={{
                    color: colorTokens.fg.muted,
                    borderColor: colorTokens.border.default,
                    backgroundColor: 'rgba(55,53,47,0.04)',
                    '&:hover': {
                      borderColor: colorTokens.fg.faint,
                      backgroundColor: 'rgba(55,53,47,0.08)',
                    },
                  }}
                >
                  RP削除
                </Button>
              )}
            </Box>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, sm: 8 }}>
                <TextField
                  fullWidth
                  label={NSIPS_LABELS.USAGE}
                  value={prescription.usage}
                  onChange={onUsageChange}
                  required
                  error={!!errors[`rp_${rpIndex}_usage`]}
                  helperText={errors[`rp_${rpIndex}_usage`]}
                  placeholder="１日１回朝食後"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label={NSIPS_LABELS.DAYS}
                  value={prescription.days}
                  onChange={onDaysChange}
                  required
                  error={!!errors[`rp_${rpIndex}_days`]}
                  helperText={errors[`rp_${rpIndex}_days`]}
                  InputProps={{
                    endAdornment: <Typography variant="body2">日分</Typography>,
                  }}
                />
              </Grid>
            </Grid>

            {prescription.drugs.map((drug, drugIndex) => (
              <Box key={drugIndex} sx={{ mb: 2, pl: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  薬品 {drugIndex + 1}:
                </Typography>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DrugSearchField
                      value={drug.name}
                      onSelect={onDrugSelect(drugIndex)}
                      error={!!errors[`rp_${rpIndex}_drug_${drugIndex}_name`]}
                      helperText={
                        errors[`rp_${rpIndex}_drug_${drugIndex}_name`]
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 2 }}>
                    <TextField
                      fullWidth
                      label={NSIPS_LABELS.QUANTITY}
                      value={drug.quantity}
                      onChange={onDrugFieldChange(drugIndex, 'quantity')}
                      required
                      error={
                        !!errors[`rp_${rpIndex}_drug_${drugIndex}_quantity`]
                      }
                      helperText={
                        errors[`rp_${rpIndex}_drug_${drugIndex}_quantity`]
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 2 }}>
                    <TextField
                      fullWidth
                      label={NSIPS_LABELS.UNIT}
                      value={drug.unit}
                      onChange={onDrugFieldChange(drugIndex, 'unit')}
                      required
                      error={!!errors[`rp_${rpIndex}_drug_${drugIndex}_unit`]}
                      helperText={
                        errors[`rp_${rpIndex}_drug_${drugIndex}_unit`]
                      }
                      placeholder="錠"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 2 }}>
                    {prescription.drugs.length > 1 && (
                      <IconButton
                        onClick={() => onRemoveDrug(drugIndex)}
                        sx={{
                          mt: 1,
                          color: colorTokens.fg.muted,
                          '&:hover': {
                            backgroundColor: 'rgba(55,53,47,0.08)',
                          },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              </Box>
            ))}

            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={onAddDrug}
              sx={{
                ml: 2,
                color: colorTokens.accent.primary,
                borderColor: colorTokens.accent.primary,
                backgroundColor: 'rgba(55,53,47,0.04)',
                '&:hover': {
                  borderColor: colorTokens.accent.primaryHover,
                  backgroundColor: 'rgba(55,53,47,0.08)',
                },
              }}
            >
              薬品を追加
            </Button>
          </CardContent>
        </Card>
      )
    },
    (prevProps, nextProps) => {
      return (
        prevProps.prescription === nextProps.prescription &&
        prevProps.errors === nextProps.errors &&
        prevProps.canDelete === nextProps.canDelete
      )
    }
  )

PrescriptionSection.displayName = 'PrescriptionSection'
